import { Component, ChangeDetectionStrategy, inject, signal, OnInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { PaymentLinkService, PaymentSuccessSummaryDTO } from '../../../services/payment-link.service';
import { TagModule } from 'primeng/tag';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-payment-link-success',
  templateUrl: './payment-link-success.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TagModule]
})
export class PaymentLinkSuccessComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private paymentLinkService = inject(PaymentLinkService);
  private destroyRef = inject(DestroyRef);

  summary = signal<PaymentSuccessSummaryDTO | null>(null);
  loading = signal(true);
  error = signal(false);

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    const trackingId = params['trackingId'] ?? '';

    if (!trackingId) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    timer(0, 5000)
      .pipe(
        switchMap(() => this.paymentLinkService.getPaymentSuccessSummary(trackingId)),
        takeWhile((res) => res.body?.paymentStatus !== 'PAID', true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          if (res?.body) {
            this.summary.set(res.body);
            this.loading.set(false);
          } else if (this.loading() && !this.summary()) {
            this.loading.set(false);
            this.error.set(true);
          }
        },
        error: () => {
          if (this.loading() && !this.summary()) {
            this.loading.set(false);
            this.error.set(true);
          }
        }
      });
  }

  formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN'
    }).format(amount);
  }

  formatDate(isoDate: string | undefined): string {
    if (!isoDate) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(isoDate));
  }
}
